/**
 * `Flagged rows` — the owner's review surface, at `/countries/:cc/licences/disputes`.
 *
 * ===========================================================================
 * THIS SCREEN IS THE POINT OF IMPORTING FLAGGED RATHER THAN DECIDING
 * ===========================================================================
 * Nine questions about this workbook are still open — seven of the owner's, plus
 * two the approved map had written down and nothing carried onto a row until the
 * prove pass. The import answered none of them: it wrote a machine-readable code
 * onto every row each question is about — 1,417 of 3,838 rows on his file — and
 * this is where those sets are handed back to him, grouped by the question, with
 * the evidence and a link into the register.
 *
 * ⚠ NOTHING HERE DECIDES ANYTHING, AND THE ABSENCE OF CONTROLS IS THE DESIGN.
 * There is no write route on the whole licence API. Closing a code is a ruling
 * followed by a re-import — a `ts-node` verb whose authorisation is database
 * access — so a button on this screen would be a promise the server cannot keep.
 * `reports.test.tsx` asserts the absence of every button, checkbox and textbox
 * rather than trusting this paragraph.
 *
 * ===========================================================================
 * TWO NUMBERS, BOTH PRINTED, AND THE REASON THEY DIFFER
 * ===========================================================================
 * A row can carry two codes (and two rows carry three), so the per-code column
 * adds up to MORE than the flagged-row headline (1,438 against 1,417 on the
 * owner's file). A screen that printed one and let the reader add up the other
 * spends the first hour of a
 * review meeting on arithmetic. Both are here, with the excess between them, and
 * the server publishes its own reconciliation — which this screen renders as a
 * WARNING when it does not hold rather than swallowing the disagreement.
 *
 * ===========================================================================
 * EVERY FIGURE IS THE SERVER'S, INCLUDING THE ZEROS
 * ===========================================================================
 * The design measured 1,417 flagged rows; this screen reads whatever THIS
 * database holds. On a machine where the import has never run it shows zero and
 * says the register is empty and who fills it — the word it must never use is
 * "loading", because there is no screen that writes to this register and the
 * state is permanent until an operator runs the import.
 *
 * ===========================================================================
 * A BATCH-LEVEL CODE PRINTS NO NUMBER
 * ===========================================================================
 * `D-9` and `D-10` cover 2,665 and 857 rows of the owner's file — the four large
 * product equivalences the review adjudicated by assertion — but the register
 * holds those decisions as the MAP it imported under, not as codes on rows. So
 * there is no row set to count, the payload type carries no count field at all,
 * and this screen prints "No row scope". A `0` would read as "this affects
 * nothing", which is the opposite of true.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Chip, EmptyState, SkeletonRows } from '../../components/ui';
import { fetchLicenceDisputeReport, licenceErrorText, licenceKeys } from './api';
import type { DisputeCodeRow } from './api';

/**
 * Grouped with an EXPLICIT locale — the same rule the register screen keeps. The
 * two figures on this screen are read against each other by eye, and `1,438`
 * beside `1.417` would be read as a typo rather than as two number systems.
 */
const GROUPED = new Intl.NumberFormat('en-US');

function n(value: number): string {
  return GROUPED.format(value);
}

export function DisputeReviewPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const canView = can(countryCode, 'quotes.view');

  const report = useQuery({
    queryKey: licenceKeys.disputes(countryCode),
    queryFn: ({ signal }) => fetchLicenceDisputeReport(countryCode, signal),
    enabled: canView,
  });

  if (!canView) {
    // The refusal is a designed state naming who to ask — not an empty screen, which
    // here would read as "nothing is flagged", a false statement about the DATA
    // rather than a true one about the account.
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="The licence register is not available to this account">
          Reviewing the flagged licence rows in {countryCode} needs the quotes permission.
          Ask an administrator to widen your access.
        </Alert>
      </div>
    );
  }

  if (report.isPending) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <PageHeader title="Flagged rows" />
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading the dispute register" />
        </div>
      </div>
    );
  }

  if (report.isError) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <PageHeader title="Flagged rows" />
        <Alert tone="danger" title="The dispute register could not be loaded">
          {/* The server's own sentence — on a 400 it names the vocabulary it refused. */}
          <p>{licenceErrorText(report.error)}</p>
        </Alert>
      </div>
    );
  }

  const data = report.data;
  const broken = data.reconciliations.filter((item) => !item.holds);

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Flagged rows"
        subtitle="Imported and flagged, never corrected. Each code is an open owner decision."
      />

      {/*
       * ⚠ A RECONCILIATION THAT DOES NOT HOLD IS SHOWN. The server publishes the
       * parts, the total and whether they add up; a screen that rendered the
       * figures and hid the disagreement would defeat the only check that catches
       * a projection quietly losing rows.
       */}
      {broken.map((item) => (
        <Alert key={item.claim} tone="danger" title="These figures do not add up">
          <p>
            {item.claim} — the parts come to {n(item.sum)} against a total of {n(item.total)}.
            The register was counted as it stands; this is a discrepancy in the data, not a
            rounding of it.
          </p>
        </Alert>
      ))}

      <div
        data-testid="dispute-headline"
        className="flex flex-wrap items-baseline gap-x-6 gap-y-1 rounded-md border border-line bg-surface px-5 py-4"
      >
        <p className="text-body text-fg">
          <span className="tnum text-h3 font-medium">{n(data.events_flagged)}</span> of{' '}
          <span className="tnum font-medium">{n(data.events_total)}</span> licence rows carry
          at least one open question
        </p>
        <p className="text-label text-fg-secondary">
          <span className="tnum font-medium">{n(data.customers_flagged)}</span> of{' '}
          <span className="tnum">{n(data.customers_total)}</span> canonical customers are
          flagged
        </p>
      </div>

      {data.events_flagged === 0 ? (
        <div className="rounded-md border border-dashed border-line bg-surface">
          <EmptyState
            title="Nothing is flagged in this country."
            description="Either the register is empty here, or every imported row carries a settled ruling. It fills when an operator runs the licence import — there is no screen that writes to it."
          />
        </div>
      ) : (
        <>
          <p
            data-testid="dispute-arithmetic"
            className="rounded-md border border-line bg-subtle px-5 py-3 text-label text-fg-secondary"
          >
            The code column below adds up to{' '}
            <span className="tnum font-medium text-fg">{n(data.code_row_sum)}</span>, which is
            more than the {n(data.events_flagged)} flagged rows, because{' '}
            <span className="tnum font-medium text-fg">{n(data.multi_code_events)}</span> rows
            carry more than one code — {n(data.excess_codes)} extra codes in all. Both numbers
            are counted from the register; neither is wrong.
          </p>

          <CodeTable countryCode={countryCode} codes={data.codes} />
        </>
      )}

      <BatchCodes codes={data.batch_codes} />
      <RefusedReports refused={data.refused_reports} />

      {data.disclosures.length === 0 ? null : (
        <ul data-testid="disclosures" className="flex flex-col gap-1.5">
          {data.disclosures.map((line) => (
            <li key={line} className="text-label text-fg-secondary">
              {line}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────── the row codes ── */

function CodeTable({
  countryCode,
  codes,
}: {
  countryCode: string;
  codes: readonly DisputeCodeRow[];
}): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[860px] text-left text-body">
        <thead className="border-b border-line text-label uppercase text-fg-muted">
          <tr>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Code
            </th>
            <th scope="col" className="px-4 py-2.5 text-right font-medium">
              Rows
            </th>
            <th scope="col" className="px-4 py-2.5 text-right font-medium">
              Serials
            </th>
            <th scope="col" className="px-4 py-2.5 text-right font-medium">
              Customers
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              What it records, and the rows it is about
            </th>
          </tr>
        </thead>
        <tbody>
          {codes.map((row) => (
            <tr key={row.code} className="crf-register-row border-b border-line last:border-b-0 align-top">
              <td className="px-4 py-2.5">
                {/*
                 * ⚠ THE DRILL-IN. The count is only checkable if the reader can open
                 * exactly the set it counts, so the code IS the link. The screen builds
                 * its own URL rather than rewriting the payload's `filter` — that one is
                 * the API query, and two different paths should not be spelled by
                 * string surgery on each other.
                 */}
                <Link
                  to={`/countries/${countryCode}/licences?dispute=${encodeURIComponent(row.code)}`}
                  className="font-mono text-label text-indigo underline-offset-2 hover:underline"
                >
                  {row.code}
                </Link>
              </td>
              <td className="tnum px-4 py-2.5 text-right font-medium">{n(row.events)}</td>
              <td className="tnum px-4 py-2.5 text-right text-fg-secondary">
                {n(row.licences)}
              </td>
              <td className="tnum px-4 py-2.5 text-right text-fg-secondary">
                {n(row.customers)}
              </td>
              <td className="px-4 py-2.5">
                {/* The importer's own note, verbatim. The screen never paraphrases it. */}
                <p className="text-label text-fg-secondary">{row.note}</p>
                {row.first_event_on === null ? null : (
                  <p className="tnum mt-1 text-label text-fg-muted">
                    {row.first_event_on} to {row.last_event_on}
                  </p>
                )}
                {row.sample.length === 0 ? null : (
                  <ul className="mt-2 flex flex-col gap-1">
                    {row.sample.map((sample) => (
                      <li
                        key={`${sample.serial}-${sample.event_on}-${String(sample.source_row)}`}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1 text-label text-fg-muted"
                      >
                        {/*
                         * The RAW workbook cells, verbatim: normalisation is a claim,
                         * and the raw string is the evidence he is being asked to rule
                         * on.
                         */}
                        <span className="tnum font-mono">{sample.serial}</span>
                        <span className="tnum">{sample.event_on}</span>
                        <span>
                          {sample.source_sheet ?? '—'}!{String(sample.source_row ?? '—')}
                        </span>
                        <span>{sample.source_date_text ?? '—'}</span>
                        <span>{sample.source_party_text ?? '—'}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ──────────────────────────────────────────── the batch-level codes ── */

function BatchCodes({
  codes,
}: {
  codes: readonly { code: string; note: string }[];
}): React.JSX.Element | null {
  if (codes.length === 0) return null;
  return (
    <section data-testid="batch-codes" className="rounded-md border border-line bg-surface p-5">
      <h2 className="text-body font-medium text-fg">Open questions with no row scope</h2>
      <p className="mt-1 text-label text-fg-secondary">
        These are properties of the whole import rather than of any row — the scope it was
        applied under, and the product equivalences the review proposed and this build did
        not apply. They carry no count because the register holds no row set for them; a
        zero here would say they affect nothing.
      </p>
      <ul className="mt-3 flex flex-col gap-3">
        {codes.map((entry) => (
          <li key={entry.code} className="flex flex-col gap-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-label text-fg">{entry.code}</span>
              <Chip>No row scope</Chip>
            </span>
            <span className="text-label text-fg-secondary">{entry.note}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ─────────────────────────────────────────── what is NOT reported ── */

/**
 * ⚠ NAMED, RATHER THAN SIMPLY MISSING.
 *
 * Two of these three are the reports a commercial reader assumes exist, and the
 * money one is the load-bearing refusal: the currency strings in this workbook are
 * PRODUCT names the owner has not declassified, so a value column would be money
 * invented from a label. A report nobody built and a report deliberately not built
 * look identical from outside, and only one of them is a decision.
 */
function RefusedReports({
  refused,
}: {
  refused: readonly { id: string; title: string; reason: string; returns_when: string | null }[];
}): React.JSX.Element | null {
  if (refused.length === 0) return null;
  return (
    <section
      data-testid="refused-reports"
      className="rounded-md border border-line bg-surface p-5"
    >
      <h2 className="text-body font-medium text-fg">Not reported, and why</h2>
      <ul className="mt-3 flex flex-col gap-3">
        {refused.map((entry) => (
          <li key={entry.id} className="flex flex-col gap-1">
            <span className="text-label font-medium text-fg">{entry.title}</span>
            <span className="text-label text-fg-secondary">{entry.reason}</span>
            <span className="text-label text-fg-muted">
              {entry.returns_when === null
                ? 'Nothing known would make this possible.'
                : `Returns when ${entry.returns_when}.`}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
